import { CaseLowerIcon, CaseSensitiveIcon, RegexIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasToggle,
  CanvasSimpleSelect,
  FieldSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  fields,
  RegexMatch,
  regexMatch,
  simpleRegexType,
  SimpleRegexShape,
  SimpleRegexProps,
  Dimensions,
  Field,
  metadataType,
  textOperators,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { CanvasRegexTextArea } from "@/components/CanvasRegexTextArea";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isSimpleRegexShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is SimpleRegexShape {
  return (
    shape.type === simpleRegexType &&
    ["match", "exclude"].includes((shape.props as SimpleRegexProps).type)
  );
}

export class SimpleRegexTool extends ToolStateNode {
  static override id = simpleRegexType;

  static override uiToolDef = (editor: Editor) => ({
    id: simpleRegexType,
    icon: RegexIcon,
    label: SimpleRegexShapeUtil.docs.title,
    kbd: "r",
    defaultColor: SimpleRegexShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(simpleRegexType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={SimpleRegex}
        util={SimpleRegexShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: simpleRegexType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class SimpleRegexShapeUtil extends BindableUtil<SimpleRegexShape> {
  static override migrations = createMigrations(simpleRegexType);
  static override type = simpleRegexType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Regex",
    description:
      "Match or exclude a regular expression pattern in an attribute's value.",
  };

  override getType() {
    return simpleRegexType;
  }

  static override props: RecordProps<SimpleRegexShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    // @ts-expect-error We want users to be able to select any field
    var: T.string,
    regex: T.string,
    type: T.literalEnum("match", "exclude"),
    insensitive: T.boolean.optional(),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 380,
      minW: 380,
      h: MIN_H,
      var: fields[0],
      regex: "\\balf\\b",
      type: regexMatch[0],
      insensitive: true,
      metadata: undefined,
    };
  }

  override component(shape: SimpleRegexShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <SimpleRegex
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function SimpleRegex({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: SimpleRegexShape;
  className?: string;
  updateShape: (
    shape: SimpleRegexShape,
    props: Partial<SimpleRegexProps>,
  ) => void;
}) {
  const variant =
    shape.props.type === "match" || shape.props.type === "any"
      ? "green"
      : "red";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={{
            ...SimpleRegexShapeUtil.docs,
            title: shape.props.insensitive ? "Regex" : "Case Sensitive Regex",
          }}
        />
        <BlockBody>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <div className="flex gap-2">
              {shape.props.insensitive ? (
                <SimpleTooltip message="No case sensitivity">
                  <div>
                    <CanvasToggle
                      variant="outline"
                      pressed={false}
                      onPressedChange={() =>
                        updateShape(shape, { insensitive: false })
                      }
                    >
                      <span className="sr-only">Make case insensitive</span>
                      <CaseLowerIcon />
                    </CanvasToggle>
                  </div>
                </SimpleTooltip>
              ) : (
                <SimpleTooltip message="Case sensitive">
                  <div>
                    <CanvasToggle
                      variant="outline"
                      pressed={false}
                      onPressedChange={() =>
                        updateShape(shape, { insensitive: true })
                      }
                    >
                      <span className="sr-only">Make case Sensitive</span>
                      <CaseSensitiveIcon />
                    </CanvasToggle>
                  </div>
                </SimpleTooltip>
              )}
              <FieldSelect
                validOperators={textOperators}
                value={shape.props.var}
                onValueChange={(v) => updateShape(shape, { var: v as Field })}
                className="flex-1"
              />
              <CanvasSimpleSelect
                aria-label="Regex Match Type"
                value={shape.props.type}
                options={[
                  { value: "match", label: "contains" },
                  { value: "exclude", label: "missing" },
                ]}
                className="w-28 shrink-0 flex-1"
                placeholder="Type"
                onValueChange={(v) =>
                  updateShape(shape, { type: v as RegexMatch })
                }
              />
            </div>
            <CanvasRegexTextArea
              value={shape.props.regex}
              onChange={(regex) => updateShape(shape, { regex })}
              isReadOnly={shape.props.isReadOnly}
            />
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
